import { useState } from 'react';
import { useStore } from '../../../store/store';
import {
  addStyle, copyStyle, removeStyle, renameStyle, updateStyle,
} from '../../../shared/theme/styleList';

const selector = ({ configuration, setConfiguration, tmp }) => ({
  configuration,
  setConfiguration,
  tmp,
});

/**
 * Machinerie commune aux onglets de modèles des box partagées avec l'éditeur
 * de site (Volet, Carte info) : liste des modèles, sélection, création,
 * duplication, renommage, suppression, écriture d'une prop.
 *
 * Les couleurs sont converties au passage : le configurateur raisonne en
 * labels de palette, le style stocke des chaînes CSS — c'est ce qui permet au
 * parser de recopier le style tel quel dans le bloc web.
 *
 * @param {string} configKey      clé du tableau de styles dans la configuration
 * @param {object} defaultStyle   style de départ d'une création
 * @param {(configuration, tmp) => Array} mergeStyles styles config + sections liées
 */
export function useBoxStyleTab({
  configKey, defaultStyle, mergeStyles,
}) {
  const { configuration, setConfiguration, tmp } = useStore(selector);
  const styleList = mergeStyles(configuration, tmp);
  const [selectedStyleId, setSelectedStyleId] = useState(styleList[0]?.id);
  const selectedStyle = styleList.find((s) => s.id === selectedStyleId) || styleList[0];

  const saveStyle = (updated) => setConfiguration(updateStyle(configuration, configKey, updated));

  return {
    configuration,
    styleList,
    selectedStyle,
    selectedStyleId: selectedStyle?.id,
    setSelectedStyleId,
    /** Style verrouillé : il vient d'une section liée, on ne l'édite pas ici. */
    locked: !!selectedStyle?.fromTmp,

    setProp: (key, value) => saveStyle({ ...selectedStyle, [key]: value }),
    setProps: (patch) => saveStyle({ ...selectedStyle, ...patch }),

    /** Chaîne CSS stockée → label de la palette, pour les sélecteurs. */
    labelOfColor: (cssColor) => configuration.colors
      .find((c) => c.style === cssColor)?.label ?? 'transparent',
    /** Label choisi → chaîne CSS stockée (transparent = pas de couleur). */
    colorOfLabel: (label) => (label === 'transparent'
      ? ''
      : configuration.colors.find((c) => c.label === label)?.style ?? ''),

    listHandlers: {
      onSelect: setSelectedStyleId,
      onAdd: (name) => {
        const { patch, style } = addStyle(configuration, configKey, { ...defaultStyle, name });
        setConfiguration(patch);
        setSelectedStyleId(style.id);
      },
      onCopy: (copiedId) => setConfiguration(copyStyle(configuration, configKey, copiedId).patch),
      onRename: (id, name) => setConfiguration(renameStyle(configuration, configKey, id, name)),
      // Aucune box à rattraper : un modèle appliqué a été RECOPIÉ dans la box,
      // qui ne le référence pas. Supprimer un modèle ne change donc rien aux
      // documents déjà mis en forme.
      onRemove: (idToRemove) => setConfiguration(removeStyle(configuration, configKey, idToRemove)),
    },
  };
}
